/**
 * URL-backed list state (filters, sort, page, page size) for table pages.
 *
 * Everything lives in the route's search params so a filtered, sorted page is
 * bookmarkable and survives refresh/back. Changing a filter or the sort resets
 * to page 1; changing the page size also resets to page 1.
 */
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import type { ListSearch } from "@/app/router";
import type { ListState } from "@/shared/lib/paging";

/** Default page size for tables. */
export const DEFAULT_PAGE_SIZE = 50;
/** Page sizes offered in the table footer. */
export const PAGE_SIZES = [25, 50, 100] as const;

/** What `useListState` returns. */
export interface ListStateApi {
  search: ListSearch;
  state: ListState;
  /** Change filters (resets to page 1). */
  update: (patch: Partial<ListSearch>) => void;
  /** Replace the server ordering (resets to page 1). */
  setSort: (sort: string | undefined) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  /** Remove every filter and sort, keep the page size. */
  clear: () => void;
}

/** Keys that are table state rather than filters. */
const TABLE_KEYS: (keyof ListSearch)[] = ["page", "size", "sort"];

/**
 * Bind the current route's search params to table state.
 *
 * @param to Route path the page lives on, used for navigation.
 * @param defaultSort Ordering applied when the URL has none.
 */
export function useListState(to: string, defaultSort?: string): ListStateApi {
  const search: ListSearch = useSearch({ strict: false });
  const navigate = useNavigate();
  const go = useCallback(
    (next: ListSearch): void =>
      void navigate({ to, search: next, replace: true, resetScroll: false }),
    [navigate, to],
  );
  const state = useMemo<ListState>(
    () => ({
      page: search.page ?? 1,
      size: search.size ?? DEFAULT_PAGE_SIZE,
      sort: search.sort ?? defaultSort,
    }),
    [search.page, search.size, search.sort, defaultSort],
  );
  return {
    search,
    state,
    update: (patch) => go({ ...search, ...patch, page: undefined }),
    setSort: (sort) => go({ ...search, sort, page: undefined }),
    setPage: (page) => go({ ...search, page: page > 1 ? page : undefined }),
    setSize: (size) =>
      go({ ...search, size: size === DEFAULT_PAGE_SIZE ? undefined : size, page: undefined }),
    clear: () => go({ size: search.size, tab: search.tab }),
  };
}

/** Count of active filter values in a search object (ignores table state and tab). */
export function activeFilterCount(search: ListSearch): number {
  return Object.entries(search).filter(
    ([k, v]) =>
      v !== undefined &&
      v !== "" &&
      v !== false &&
      !TABLE_KEYS.includes(k as keyof ListSearch) &&
      k !== "tab",
  ).length;
}
